'use client';

import { useMemo } from 'react';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import type { FloorplanMedia } from '../floorplanMedia';

type Unit = {
  modelId: string;
  name: string;
  availabilityDate: string;
  beds: number;
  baths: number;
  sqft: number;
  low: number;
  high: number;
  priceRangeDisplay: Array<string>;
  isNewUnit?: boolean;
  createdAgo?: string;
  priceDrop?: number;
};

export type Floorplan = FloorplanMedia & {
  modelId: string;
  name: string;
  type: string;
  availabilityDate: string;
  beds: number;
  baths: number;
  sqft: number;
  low: number;
  high: number;
  priceRangeDisplay: Array<string>;
  units: Array<Unit>;
  zillow3dTourUrl: string;
};

export type BedroomOption = {
  beds: number;
  label: string;
};

const getBedroomLabel = (beds: number): string => {
  if (beds === 0) return 'Studio';
  if (beds === 1) return '1 Bed';
  return `${beds} Beds`;
};

const getSortablePrice = ({ low }: { low: number }): number => (Number.isFinite(low) && low > 0 ? low : Infinity);

const compareNumbers = (a: number, b: number): number => {
  if (a === b) return 0;
  return a < b ? -1 : 1;
};

const comparePrices = (a: { low: number }, b: { low: number }): number =>
  compareNumbers(getSortablePrice(a), getSortablePrice(b));

// Rank by the unit rows the card shows: the v3 adapter fills an unpriced floorplan's `low` from the building summary.
const getFloorplanSortKey = ({ units }: Floorplan): [rank: number, price: number] => {
  const lowestUnitPrice = Math.min(...units.map(getSortablePrice));
  if (lowestUnitPrice < Infinity) return [0, lowestUnitPrice];
  return [units.length > 0 ? 1 : 2, Infinity];
};

const compareFloorplans = (a: Floorplan, b: Floorplan): number => {
  const [aRank, aPrice] = getFloorplanSortKey(a);
  const [bRank, bPrice] = getFloorplanSortKey(b);
  return aRank - bRank || compareNumbers(aPrice, bPrice);
};

export const useFloorplansByBedroom = (listing: PropertyDetailListing | null | undefined) => {
  const floorplans = (listing as unknown as { floorplans?: Array<Floorplan> } | null | undefined)?.floorplans;

  const { bedroomOptions, floorplansByBedroom } = useMemo(() => {
    if (!floorplans || floorplans.length === 0) {
      return {
        bedroomOptions: [] as Array<BedroomOption>,
        floorplansByBedroom: new Map<number, Array<Floorplan>>(),
      };
    }

    const grouped = new Map<number, Array<Floorplan>>();

    for (const fp of floorplans) {
      const beds = Number(fp.beds);
      if (!Number.isFinite(beds)) continue;
      const normalized = { ...fp, beds, units: [...(fp.units ?? [])].sort(comparePrices) };
      const existing = grouped.get(beds) ?? [];
      existing.push(normalized);
      grouped.set(beds, existing);
    }

    for (const floorplansForBedroom of grouped.values()) {
      floorplansForBedroom.sort(compareFloorplans);
    }

    const sortedBeds = Array.from(grouped.keys()).sort((a, b) => a - b);
    const options: Array<BedroomOption> = sortedBeds.map((beds) => ({
      beds,
      label: getBedroomLabel(beds),
    }));

    return { bedroomOptions: options, floorplansByBedroom: grouped };
  }, [floorplans]);

  return {
    bedroomOptions,
    floorplansByBedroom,
    hasFloorplans: bedroomOptions.length > 0,
  };
};
